import { MouseEvent, PropsWithChildren, useMemo, useState, useEffect, useRef, FC } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { ListItem } from '@ravago/shared/radiance/components/list/components/ListItem/ListItem';
import { RangeSlider } from '@ravago/shared/radiance/components/slider/components/RangeSlider/RangeSlider';
import { HighlightText } from '@ravago/shared/radiance/components/typography/components/HighlightText/HighlightText';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';

import { FacetContentV2 } from '../../../models/facet-group.model';
import { propertyUnitSuffix } from '../../../utils/v2-mappers-utils';

export interface CheckedChangeEventV2 {
   checked: boolean;
}

export interface RangeChangeEventV2 {
   min: number;
   max: number;
}

interface Props extends PropsWithChildren {
   label: string;
   highlightValue: string;
   facet: FacetContentV2;
   checked: boolean;
   indeterminate: boolean;
   range?: { min: number; max: number };
   disabled: boolean;
   onCheckedChange: (event: CheckedChangeEventV2) => void;
   onRangeChange?: (event: RangeChangeEventV2) => void;
}

export const FacetTermV2: FC<Props> = ({
   label,
   highlightValue,
   facet,
   checked,
   indeterminate,
   range,
   disabled,
   onCheckedChange,
   onRangeChange,
   children
}) => {
   const sliderDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const [expanded, setExpanded] = useState(false);
   const [minInput, setMinInput] = useState<string | undefined>(
      facet.range?.min !== undefined && range?.min !== undefined && facet.range.min < range.min ? range.min.toString() : undefined
   );
   const [maxInput, setMaxInput] = useState<string | undefined>(
      facet.range?.max !== undefined && range?.max !== undefined && facet.range.max > range.max ? range.max.toString() : undefined
   );

   const setMinMaxFromRange = (newRange: { min: number; max: number }) => {
      if (range?.min !== undefined && range.min !== newRange.min && minInput !== newRange.min.toString()) setMinInput(newRange.min.toString());
      if (range?.max !== undefined && range.max !== newRange.max && maxInput !== newRange.max.toString()) setMaxInput(newRange.max.toString());
   };

   const onSliderChange = (state: { min: number; max: number }) => {
      if (facet.rangeDisplay?.hasFillInBoxes) {
         setMinMaxFromRange(state);
      }
      if (sliderDebounceRef.current) clearTimeout(sliderDebounceRef.current);
      sliderDebounceRef.current = setTimeout(() => {
         onRangeChange?.({ min: state.min, max: state.max });
      }, 300);
   };

   // update on min max of a facet changing
   useEffect(() => {
      if (facet.rangeDisplay?.hasFillInBoxes) {
         if (facet.range?.min !== undefined && range?.min !== undefined && facet.range.min > range.min) setMinInput(facet.range.min.toString());
         if (facet.range?.max !== undefined && range?.max !== undefined && facet.range.max < range.max) setMaxInput(facet.range.max.toString());
      }
   }, [facet.range]);

   useEffect(() => {
      if (facet.rangeDisplay?.hasFillInBoxes && !checked) {
         setMinInput(undefined);
         setMaxInput(undefined);
      }
   }, [checked]);

   const onInputBoxChange = () => {
      const rawMinInput = minInput ? parseFloat(minInput) : range?.min;
      const rawMaxInput = maxInput ? parseFloat(maxInput) : range?.max;

      if (rawMaxInput === undefined || rawMinInput === undefined) return;
      if (facet.range?.min === undefined || facet.range?.max === undefined) return;

      let adjustedMinInput = Math.max(facet.range.min, rawMinInput);
      const adjustedMaxInput = Math.min(facet.range.max, rawMaxInput);
      if (adjustedMinInput > adjustedMaxInput) {
         adjustedMinInput = adjustedMaxInput;
      }
      if (rawMaxInput !== adjustedMaxInput) {
         setMaxInput(adjustedMaxInput.toString());
      }
      if (rawMinInput !== adjustedMinInput) {
         setMinInput(adjustedMinInput.toString());
      }
      onRangeChange?.({ min: adjustedMinInput, max: adjustedMaxInput });
   };

   const onCheckboxChange = () => {
      if (checked) {
         setExpanded(false);
      } else {
         setExpanded(true);
      }
      onCheckedChange({ checked: indeterminate ? true : !checked });
   };

   const onListItemClicked = (e: MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement | null;
      if (children && highlightValue === '' && target?.tagName === 'I') {
         setExpanded((curr) => !curr);
         return;
      }

      onCheckboxChange();
   };

   const secondaryActionIcon = useMemo(() => {
      if (!children || highlightValue !== '') return undefined;

      return expanded ? 'chevron-up' : 'chevron-down';
   }, [children, expanded, highlightValue]);

   return (
      <Box direction="column" width="100%" gap="xs">
         <ListItem disabled={disabled && !checked} variant="transparent" onClick={onListItemClicked} trailingIcon={secondaryActionIcon}>
            <Checkbox id={facet.key} checked={checked} indeterminate={indeterminate} gutter={false} disabled={disabled && !checked}>
               <Typography variant="body" color={disabled && !checked ? 'weak' : 'strong'}>
                  <span style={{ marginRight: '10px' }}>
                     <HighlightText text={label} searchValue={highlightValue} />
                  </span>
                  {!facet.hideCount && facet.count !== -1 && (
                     <Typography inline component="span" variant="caption" color="subtle">
                        ({facet.count})
                     </Typography>
                  )}
               </Typography>
            </Checkbox>
         </ListItem>

         {!!children && <Expand expanded={expanded || highlightValue !== ''}>{children}</Expand>}

         {checked && facet.range && facet.rangeDisplay?.hasFillInBoxes && (
            <Box direction="row" width="100%" justify="between" gap="xs">
               <InputField
                  min={facet.range.min}
                  max={facet.range.max}
                  hideStepper
                  textSize="small"
                  type="number"
                  value={minInput}
                  suffix={facet.unit && propertyUnitSuffix[facet.key] ? propertyUnitSuffix[facet.key][facet.unit] : undefined}
                  weakSuffix
                  placeHolder={facet.range.min?.toString()}
                  supportText={`Min value: ${facet.range.min}`}
                  onChange={(change) => setMinInput(change)}
                  onBlur={onInputBoxChange}
                  onEnter={onInputBoxChange}
               />
               <InputField
                  min={facet.range.min}
                  max={facet.range.max}
                  hideStepper
                  textSize="small"
                  suffix={facet.unit && propertyUnitSuffix[facet.key] ? propertyUnitSuffix[facet.key][facet.unit] : undefined}
                  weakSuffix
                  type="number"
                  value={maxInput}
                  placeHolder={facet.range.max?.toString()}
                  supportText={`Max value: ${facet.range.max}`}
                  onChange={(change) => setMaxInput(change)}
                  onBlur={onInputBoxChange}
                  onEnter={onInputBoxChange}
               />
            </Box>
         )}
         {checked && facet.range && facet.rangeDisplay?.useSlider && (
            <Box direction="column" width="100%" spacing={{ left: 'xs', right: 'xs', top: 'xs', bottom: 'xs' }}>
               <RangeSlider
                  min={facet.range.min}
                  max={facet.range.max}
                  step={facet.rangeDisplay?.step}
                  showValue={!facet.rangeDisplay?.hasFillInBoxes}
                  value={range}
                  onChange={onSliderChange}
               />
            </Box>
         )}
      </Box>
   );
};
